import { FC } from 'react'
import { ArrowLeft, LogIn, LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useAuthStore } from '@/store/auth.store'
import { loginWithAuth0, logoutWithAuth0 } from '@/services/auth0Service'

interface HeaderProps {
  title: string
  showBack?: boolean
  /**
   * Explicit destination of the back arrow. The browser history is not a
   * reliable way back here: a QR scan opens a page directly, so `navigate(-1)`
   * would leave the app. Pages that know where they sit in the hierarchy pass
   * their parent instead.
   */
  backTo?: string
}

const Header: FC<HeaderProps> = ({ title, showBack = false, backTo }) => {
  const navigate = useNavigate()
  const { language, setLanguage, t } = useTranslation()
  const { isAuthenticated } = useAuthStore()

  const handleAuth = async () => {
    if (isAuthenticated) {
      await logoutWithAuth0()
    } else {
      await loginWithAuth0()
    }
  }

  return (
    <header className="sticky top-0 z-40 bg-[var(--brand-dark)] text-white">
      <div className="flex items-center justify-between h-14 px-4">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {(showBack || backTo) && (
            <button
              onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
              className="flex-shrink-0 p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors"
              aria-label={t('HEADER_BACK')}
              title={t('HEADER_BACK')}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <h1 className="text-base font-semibold truncate">{title}</h1>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            className="bg-white/10 text-white text-xs rounded-md px-2 py-1 border-0 outline-none cursor-pointer"
            value={language}
            onChange={(e) => setLanguage(e.target.value as 'en' | 'it')}
          >
            <option value="en" className="text-[var(--brand-dark)]">EN</option>
            <option value="it" className="text-[var(--brand-dark)]">IT</option>
          </select>

          <button
            onClick={handleAuth}
            className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              isAuthenticated
                ? 'text-white/80 hover:bg-white/10'
                : 'bg-[var(--brand-blue)] text-[var(--brand-dark)] hover:opacity-90'
            }`}
            title={isAuthenticated ? t('BOTTOM_NAV_LOGOUT') : t('BOTTOM_NAV_LOGIN')}
          >
            {isAuthenticated ? <LogOut className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
            <span>{isAuthenticated ? t('BOTTOM_NAV_LOGOUT') : t('BOTTOM_NAV_LOGIN')}</span>
          </button>
        </div>
      </div>
    </header>
  )
}

export default Header
